import {
  Box,
  Collapse,
  Divider,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  getPrimaryNavigation,
  getSecondaryNavigation,
} from '../components.helpers';
import { IAppNavigationMenuItem } from '../components.interfaces';
import { DrawerToRenderProps } from './navigationDrawer.interfaces';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { pages } from '../../../utils/common';
import { useTranslation } from 'src/hooks/useTranslationHook';
import { camelCase, snakeCase } from 'lodash-es';
import { useRbacState } from '../../../context/rbac.context';
import {
  hasReadPermission,
  hasAnySubmoduleReadPermission,
} from '../../../utils/rbacHelpers';
import {
  isNavItemActive,
  isNavItemExactActive,
} from './navigationDrawer.pathUtils';
import {
  buildSubmenuExpandedFromRoute,
  computeSubmenuToggleState,
} from './navigationDrawer.submenu.utils';

type pageStatusType = { [key: string]: boolean };

const getTranslationKey = (pagePath: string, parentRoute?: string): string => {
  const pathWithoutSlash = pagePath.replace(/^\//, '');

  // Handle special cases
  if (pathWithoutSlash === 'logout') {
    return 'common.logout';
  }

  // Handle sub-pages (when parentRoute is provided)
  if (parentRoute) {
    const parentPath = parentRoute.replace(/^\//, '');
    return `${parentPath}.tabs.${snakeCase(pagePath.replaceAll('-', ''))}`;
  }
  // Default: use path.title for top-level pages
  return `${pathWithoutSlash}.title`;
};

/**
 * Maps page path to RBAC module and submodule
 */
const getRbacMapping = (
  path: string
): { module: string; submodule?: string } | null => {
  const pathWithoutSlash = path.replace(/^\//, '');

  // Map paths to RBAC modules/submodules
  const pathToRbacMap: Record<string, { module: string; submodule?: string }> =
  {
    dashboard: { module: 'dashboard' },
    students: { module: 'students', submodule: 'studentList' },
    staff: { module: 'staff', submodule: 'staffList' },
    academics: { module: 'academics' },
    communications: { module: 'communications' },
    finance: { module: 'finance' },
    analytics: { module: 'analytics' },
    administration: { module: 'administration' },
    users: { module: 'users' },
    settings: { module: 'settings' },
    profile: { module: 'profile', submodule: 'personalDetails' },
  };

  return pathToRbacMap[pathWithoutSlash] || null;
};

/**
 * Maps dashboard sub-pages to RBAC submodules
 */
const getDashboardSubmoduleMapping = (path: string): string | null => {
  const pathWithoutSlash = path.replace(/^\//, '');
  const dashboardSubmoduleMap: Record<string, string> = {
    finance: 'finance',
    people: 'people',
    events: 'events',
  };
  return dashboardSubmoduleMap[pathWithoutSlash] || null;
};

/**
 * Checks if a navigation item should be visible based on RBAC permissions
 */
const shouldShowNavigationItem = (
  item: IAppNavigationMenuItem,
  rbac: any,
  parentPath?: string
): boolean => {
  if (!rbac) return false;

  const path = item.pageToNavigate.replace(/^\//, '');

  // Always show logout
  if (path === 'logout') return true;

  // Handle dashboard sub-items (finance, people, events under dashboard)
  if (parentPath && parentPath.replace(/^\//, '') === 'dashboard') {
    const submodule = getDashboardSubmoduleMapping(item.pageToNavigate);
    if (submodule) {
      return hasReadPermission(rbac, 'dashboard', submodule);
    }
  }

  const rbacMapping = getRbacMapping(item.pageToNavigate);
  if (rbacMapping) {
    // If it's a module with submodules, check if user has access to any submodule
    if (!rbacMapping.submodule) {
      return hasAnySubmoduleReadPermission(rbac, rbacMapping.module);
    }

    // Check specific submodule permission
    return hasReadPermission(rbac, rbacMapping.module, rbacMapping.submodule);
  }

  // Sub-routes like /staff-list, /lesson-plans — not in pathToRbacMap; resolve via parent module + camelCase segment (matches RBAC keys, e.g. staffList, lessonPlans)
  if (parentPath) {
    const parentMapping = getRbacMapping(parentPath);
    if (parentMapping) {
      const segment = path.split('/').pop() ?? '';
      const submoduleKey = camelCase(segment);
      if (submoduleKey) {
        return hasReadPermission(rbac, parentMapping.module, submoduleKey);
      }
    }
  }

  return false;
};

/**
 * Filters navigation items based on RBAC permissions
 */
const filterNavigationItems = (
  items: IAppNavigationMenuItem[],
  rbac: any,
  parentPath?: string
): IAppNavigationMenuItem[] => {
  return items
    .filter((item) => shouldShowNavigationItem(item, rbac, parentPath))
    .map((item) => {
      if (item.subPages) {
        const filteredSubPages = filterNavigationItems(
          item.subPages,
          rbac,
          item.pageToNavigate
        );
        // Only show parent if it has accessible subpages or if parent itself is accessible
        if (
          filteredSubPages.length > 0 ||
          shouldShowNavigationItem(item, rbac, parentPath)
        ) {
          return {
            ...item,
            subPages: filteredSubPages,
          };
        }
        return null;
      }
      return item;
    })
    .filter((item): item is IAppNavigationMenuItem => item !== null);
};

const DrawerToRender: FC<DrawerToRenderProps> = (
  props: DrawerToRenderProps
) => {
  const { selectedPage, isMenuOpen } = props;
  const { translateText } = useTranslation();
  const { rbac, isLoading } = useRbacState();
  const theme = useTheme();
  /** Align with TabGroup: tabs hidden below lg, sub-nav shows in drawer in the same range. */
  const isBelowLargeScreen = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();
  const allPrimaryNavigation = useMemo(() => getPrimaryNavigation(), []);
  const allSecondaryNavigation = useMemo(() => getSecondaryNavigation(), []);

  const mainMenuPrimarySection = useMemo(() => {
    if (isLoading || !rbac) return [];
    return filterNavigationItems(allPrimaryNavigation, rbac);
  }, [allPrimaryNavigation, rbac, isLoading]);

  const mainMenuSecondarySection = useMemo(() => {
    if (isLoading || !rbac) return [];
    return filterNavigationItems(allSecondaryNavigation, rbac);
  }, [allSecondaryNavigation, rbac, isLoading]);

  const [pagesWithSubmenu, setPagesWithSubmenu] = useState<pageStatusType>({});

  const handlePageNavigation = useCallback(
    (path: string) => {
      if (pages.auth.path === path || pages.logout.path === path) {
        sessionStorage.clear();
        localStorage.removeItem('userRole');
        localStorage.removeItem('token');
        localStorage.removeItem('instituteId');
      }
      navigate(path);
    },
    [navigate]
  );

  const updateSubMenuStatus = useCallback(
    (pageToNavigate: string, isAlreadyExpanded = false) => {
      setPagesWithSubmenu({
        ...computeSubmenuToggleState(
          mainMenuPrimarySection,
          '',
          location.pathname,
          pageToNavigate,
          isAlreadyExpanded
        ),
        ...computeSubmenuToggleState(
          mainMenuSecondarySection,
          '',
          location.pathname,
          pageToNavigate,
          isAlreadyExpanded
        ),
      });
    },
    [
      mainMenuPrimarySection,
      mainMenuSecondarySection,
      location.pathname,
    ]
  );

  const onUpdatePagesWithSubmenu = useCallback(() => {
    setPagesWithSubmenu({
      ...buildSubmenuExpandedFromRoute(
        mainMenuPrimarySection,
        '',
        location.pathname,
        selectedPage
      ),
      ...buildSubmenuExpandedFromRoute(
        mainMenuSecondarySection,
        '',
        location.pathname,
        selectedPage
      ),
    });
  }, [
    mainMenuPrimarySection,
    mainMenuSecondarySection,
    selectedPage,
    location.pathname,
  ]);

  const getEachListItem = useCallback(
    (
      item: IAppNavigationMenuItem,
      indentLevel: number,
      parentRoute: string
    ) => {
      const currPage = item.pageToNavigate.replace('/', '');
      const itemFullPath = parentRoute + item.pageToNavigate;
      const isSelected =
        isBelowLargeScreen && item.subPages
          ? isNavItemExactActive(location.pathname, itemFullPath)
          : isNavItemActive(location.pathname, itemFullPath);
      return (
        <ListItemButton
          key={item.pageToNavigate}
          selected={isSelected}
          data-pl={indentLevel}
          data-testid={`drawer-item--${currPage}`}
          data-selected={isSelected}
          sx={{
            pl: isMenuOpen
              ? `calc(14px + ${(indentLevel - 1) * 12}px)`
              : '14px',
          }}
          onClick={() => {
            if (isBelowLargeScreen && item?.subPages) {
              updateSubMenuStatus(
                item.pageToNavigate,
                pagesWithSubmenu[item.pageToNavigate]
              );
              return;
            }
            item.pageToNavigate !== selectedPage &&
              handlePageNavigation(parentRoute + item.pageToNavigate);
          }}
        >
          <ListItemIcon>{item.icon}</ListItemIcon>
          {isMenuOpen ? (
            <>
              <ListItemText
                primary={translateText(
                  getTranslationKey(item.pageToNavigate, parentRoute)
                )}
                data-testid={`drawer-item-text-${item.pageToNavigate.replace('/', '-')}`}
              />
              {isBelowLargeScreen &&
                (pagesWithSubmenu[item.pageToNavigate] ? (
                  <ExpandLess />
                ) : item.subPages ? (
                  <ExpandMore />
                ) : null)}
            </>
          ) : null}
        </ListItemButton>
      );
    },
    [
      handlePageNavigation,
      isMenuOpen,
      pagesWithSubmenu,
      selectedPage,
      location.pathname,
      translateText,
      isBelowLargeScreen,
      updateSubMenuStatus,
    ]
  );

  const getEachListItemWithSubMenu = useCallback(
    (
      item: IAppNavigationMenuItem,
      indentLevel: number,
      parentRoute: string
    ) => (
      <Fragment>
        {getEachListItem(item, indentLevel, parentRoute)}
        {isBelowLargeScreen && item?.subPages ? (
          <Collapse
            in={pagesWithSubmenu[item.pageToNavigate]}
            timeout="auto"
            unmountOnExit
            data-testid={`drawer-item-sub-menu-${item.pageToNavigate.replace('/', '-')}`}
          >
            <List component="div" disablePadding>
              {item?.subPages?.map((subPagesItem: IAppNavigationMenuItem) =>
                subPagesItem.subPages
                  ? getEachListItemWithSubMenu(
                    subPagesItem,
                    indentLevel + 1,
                    parentRoute + item.pageToNavigate
                  )
                  : getEachListItem(
                    subPagesItem,
                    indentLevel + 1,
                    parentRoute + item.pageToNavigate
                  )
              )}
            </List>
          </Collapse>
        ) : null}
      </Fragment>
    ),
    [pagesWithSubmenu, getEachListItem, isBelowLargeScreen]
  );

  useEffect(() => {
    onUpdatePagesWithSubmenu();
    // eslint-disable-next-line
  }, [
    mainMenuPrimarySection,
    mainMenuSecondarySection,
    selectedPage,
    onUpdatePagesWithSubmenu,
  ]);

  useEffect(() => {
    // Only navigate if the first param is a valid page path (starts with /)
    // Skip route parameters like studentId, tab, etc. that are not page paths
    const firstParamKey = Object.keys(params)[0];
    const firstParamValue = params[firstParamKey];
    // Route parameters like studentId, tab should not be treated as page paths
    const routeParamKeys = ['studentId', 'tab', 'studentSubRouteId', 'profileSubRouteId'];
    if (firstParamValue && typeof firstParamValue === 'string' && firstParamValue.startsWith('/') && !routeParamKeys.includes(firstParamKey)) {
      handlePageNavigation(firstParamValue);
    }
    // eslint-disable-next-line
  }, []);

  return (
    <Box>
      <List>
        {mainMenuPrimarySection.map((primaryItem: IAppNavigationMenuItem) =>
          !primaryItem?.subPages ? (
            <Fragment key={primaryItem.pageToNavigate}>
              {getEachListItem(primaryItem, 1, '')}
            </Fragment>
          ) : (
            <Fragment key={primaryItem.pageToNavigate}>
              {getEachListItemWithSubMenu(primaryItem, 1, '')}
            </Fragment>
          )
        )}
      </List>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />
      <List>
        {mainMenuSecondarySection.map(
          (secondaryItem: IAppNavigationMenuItem) =>
            !secondaryItem?.subPages ? (
              <Fragment key={secondaryItem.pageToNavigate}>
                {getEachListItem(secondaryItem, 1, '')}
              </Fragment>
            ) : (
              <Fragment key={secondaryItem.pageToNavigate}>
                {getEachListItemWithSubMenu(secondaryItem, 1, '')}
              </Fragment>
            )
        )}
      </List>
    </Box>
  );
};

export default DrawerToRender;
